/**
 * Petal Overlay Effect
 * Premium SVG-based falling cherry blossom animation with dramatic physics.
 * Features: wind gusts, tumbling rotation, spiral descent, color tinting, visibility detection.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import petalSvg from '../../assets/events/sakura/petals.svg'
import {
  OVERLAY_MAX_DPR,
  OVERLAY_TARGET_FPS,
  bucketScale,
  createAdaptiveQuality,
  createFrameGate,
  createSpriteCache,
} from './overlayPerf'

// Kill-switch for the petal animation. Perf is guarded by overlayPerf (DPR
// cap, 30fps throttle, adaptive quality that stops the overlay on low-spec
// devices); flip to false to hard-disable while keeping the static sakura
// theme CSS.
const PETAL_ANIMATION_ENABLED: boolean = true

type Petal = {
  x: number
  y: number
  scale: number
  speedY: number
  speedX: number
  baseSpeedX: number
  sway: number
  swaySpeed: number
  swayOffset: number
  rotation: number
  rotationSpeed: number
  tumbleActive: boolean
  tumbleProgress: number
  tumbleDirection: number
  spiralPath: boolean
  spiralAmplitude: number
  spiralPhase: number
  alpha: number
  hueShift: number
  saturationShift: number
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n))

const createPetal = (canvasWidth: number, canvasHeight: number): Petal => {
  const scale = 0.3 + Math.random() * 0.7 // 0.3 to 1.0 (adjusted max size)
  const baseSpeed = 0.55 + Math.random() * 0.8
  const spiralPath = Math.random() < 0.3 // 30% follow spiral paths

  return {
    x: Math.random() * canvasWidth,
    y: -20 - Math.random() * canvasHeight * 0.3,
    scale,
    speedY: baseSpeed * (0.4 + scale * 0.9), // Larger petals fall faster
    speedX: -0.18 + Math.random() * 0.36,
    baseSpeedX: -0.18 + Math.random() * 0.36,
    sway: 7 + Math.random() * 12,
    swaySpeed: 0.006 + Math.random() * 0.01,
    swayOffset: Math.random() * Math.PI * 2,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (-0.04 + Math.random() * 0.08) * (1 / scale), // Smaller petals rotate faster
    tumbleActive: false,
    tumbleProgress: 0,
    tumbleDirection: Math.random() < 0.5 ? 1 : -1,
    spiralPath,
    spiralAmplitude: spiralPath ? 15 + Math.random() * 25 : 0,
    spiralPhase: Math.random() * Math.PI * 2,
    alpha: 0.7 + scale * 0.3, // Larger = more opaque (depth perception)
    hueShift: -15 + Math.random() * 30, // ±15 degrees
    saturationShift: -10 + Math.random() * 20, // ±10%
  }
}

const drawPetal = (
  ctx: CanvasRenderingContext2D,
  petal: Petal,
  sprite: HTMLCanvasElement,
  dpr: number,
) => {
  const flip = petal.tumbleActive ? Math.cos(petal.tumbleProgress * Math.PI) : 1
  const fx = petal.tumbleDirection === 1 ? flip : 1
  const fy = petal.tumbleDirection === -1 ? flip : 1
  const cos = Math.cos(petal.rotation)
  const sin = Math.sin(petal.rotation)

  ctx.globalAlpha = petal.alpha
  ctx.setTransform(
    dpr * fx * cos,
    dpr * fx * sin,
    -dpr * fy * sin,
    dpr * fy * cos,
    dpr * petal.x,
    dpr * petal.y,
  )
  // Blit the pre-rasterized sprite
  ctx.drawImage(sprite, -sprite.width / 2, -sprite.height / 2)
}

const PetalOverlay = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const petalsRef = useRef<Petal[]>([])
  const tickRef = useRef<number>(0)

  const [petalImage, setPetalImage] = useState<HTMLImageElement | null>(null)
  const isVisibleRef = useRef(true)
  const mouseRef = useRef({ x: -1000, y: -1000 }) // Track mouse position

  const event = useCurrentEvent()

  const shouldRender = useMemo(() => {
    if (!PETAL_ANIMATION_ENABLED) return false
    if (event !== EventType.SAKURA) return false
    if (typeof window === 'undefined') return false
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches
    return !reduceMotion
  }, [event])

  // Load SVG as image (only while the overlay may render)
  useEffect(() => {
    if (!shouldRender) return
    const img = new Image()
    img.onload = () => setPetalImage(img)
    img.src = petalSvg
  }, [shouldRender])

  // Page visibility detection
  useEffect(() => {
    if (!shouldRender) return
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [shouldRender])

  // Mouse tracking
  useEffect(() => {
    if (!shouldRender) return
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }
    const handleMouseLeave = () => {
      mouseRef.current.x = -1000
      mouseRef.current.y = -1000
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [shouldRender])

  useEffect(() => {
    if (!shouldRender) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    if (!petalImage) return

    const spriteFor = createSpriteCache(petalImage)
    const dpr = clamp(window.devicePixelRatio || 1, 1, OVERLAY_MAX_DPR)
    let quality = createAdaptiveQuality(0)

    const resize = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // Optimized petal count for smaller petals: 18-45 range
      const petalCount = clamp(Math.floor((w * h) / 65000), 18, 45)
      petalsRef.current = Array.from({ length: petalCount }, () =>
        createPetal(w, h),
      )
      quality = createAdaptiveQuality(petalCount)
    }

    resize()
    canvas.style.display = ''
    let resizeTimer: number | undefined
    const onResize = () => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(resize, 150)
    }
    window.addEventListener('resize', onResize)

    const gate = createFrameGate(1000 / OVERLAY_TARGET_FPS)
    let last = performance.now()

    const animate = (now: number) => {
      rafRef.current = requestAnimationFrame(animate)

      if (!isVisibleRef.current) {
        last = now
        return
      }

      const interval = now - last
      if (!gate(now)) return

      const canvasW = window.innerWidth
      const canvasH = window.innerHeight
      const dt = clamp(interval / 16.67, 0.5, 3)
      last = now
      tickRef.current += dt
      const workStart = performance.now()

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, canvasW, canvasH)

      const particles = petalsRef.current
      for (let i = 0; i < particles.length; i++) {
        const petal = particles[i]
        // Mouse repulsion effect
        const dx = petal.x - mouseRef.current.x
        const dy = petal.y - mouseRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const repelRadius = 80 // Radius of mouse influence

        if (distance < repelRadius && distance > 0) {
          const force = (1 - distance / repelRadius) * 3 // Stronger when closer
          petal.x += (dx / distance) * force * dt
          petal.y += (dy / distance) * force * dt
        }

        // Update position
        petal.y += petal.speedY * dt

        // Spiral path motion
        if (petal.spiralPath) {
          petal.spiralPhase += 0.02 * dt
          const spiralOffset =
            Math.sin(petal.spiralPhase) *
            petal.spiralAmplitude *
            (petal.y / canvasH)
          petal.x += (petal.speedX + spiralOffset * 0.1) * dt
        } else {
          // Regular sway motion
          petal.x +=
            (petal.speedX +
              Math.sin(tickRef.current * petal.swaySpeed + petal.swayOffset) *
                petal.sway *
                0.01) *
            dt
        }

        // Rotation
        petal.rotation += petal.rotationSpeed * dt

        // Tumbling logic
        if (petal.tumbleActive) {
          petal.tumbleProgress += dt * 0.05
          if (petal.tumbleProgress >= 1) {
            petal.tumbleActive = false
            petal.tumbleProgress = 0
          }
        } else if (petal.speedY > 1.0 && Math.random() < 0.005 * dt) {
          // 50% chance per second to start tumbling when falling fast
          petal.tumbleActive = true
          petal.tumbleProgress = 0
        }

        // Reset petals that fall off screen
        if (petal.y > canvasH + 20) {
          Object.assign(petal, createPetal(canvasW, canvasH))
          petal.y = -20
        }
        if (petal.x < -20) petal.x = canvasW + 20
        if (petal.x > canvasW + 20) petal.x = -20

        // Decay wind influence gradually
        petal.speedX = petal.speedX * 0.98 + petal.baseSpeedX * 0.02

        const w = 13 * bucketScale(petal.scale)
        const h = 16 * bucketScale(petal.scale)
        drawPetal(ctx, petal, spriteFor(w, h), dpr)
      }

      ctx.globalAlpha = 1
      quality.record(performance.now() - workStart, interval)

      if (quality.stopped) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        canvas.style.display = 'none'
        return
      }

      const target = quality.count
      while (particles.length > target) particles.pop()
      while (particles.length < target)
        particles.push(createPetal(canvasW, canvasH))
    }

    rafRef.current = requestAnimationFrame((now) => {
      last = now
      animate(now)
    })

    return () => {
      window.clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [shouldRender, petalImage])

  if (!shouldRender) return null

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 9999 }}
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  )
}

export default PetalOverlay
